import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi from "util/api"

import { GetTransactionsTableResponse, ITransactionService, TransactionActionResponse } from "./type"

class TransactionService implements ITransactionService {
  public async getTransactionsTable(
    page: number,
    pageSize: number,
    id: number | undefined,
    {
      type,
      status,
      coin,
      createdAt,
      betTypes
    }: { type: string; status: string; coin?: string; createdAt?: (Date | null)[]; betTypes?: string[] },
    sortRow?: string,
    sortOrder?: string
  ): Promise<GetTransactionsTableResponse | void> {
    const params = {
      page,
      pageSize,
      type: type || null,
      status: status || null,
      createdAt: createdAt || null,
      sortRow,
      sortOrder,
      ...(coin && { coin }),
      ...(betTypes && betTypes.length > 0 && { betTypes })
    }
    const { data } = await useApi.get<IResponse<GetTransactionsTableResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.GET, { params })

    if (data?.success) {
      return data?.response
    }
  }

  public async confirmTransaction(id: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.CONFIRM, { id })
    if (data.success) {
      return data?.response?.success
    }
  }

  public async declineTransaction(id: string, reason?: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.TRANSACTIONS.DECLINE, { id, reason })

    if (data.success) {
      return data?.response?.success
    }
  }

  public async approveBankTransfer(id: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.BANK_TRANSFER.APPROVE, { id, status })
    if (data.success) {
      return data?.response?.success
    }
  }

  public async approvePayadmitTransaction(id: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.PAYADMIT.APPROVE, { id, status })
    if (data.success) return data?.response?.success
  }

  public async declinePayadmitTransaction(id: string, reason: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.PAYADMIT.DECLINE, { id, status, reason })
    if (data.success) return data?.response?.success
  }

  public async approveCryptoTransaction(id: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.CRYPTO.APPROVE, { id, status })
    if (data.success) return data?.response?.success
  }

  public async declineCryptoTransaction(id: string, reason: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.CRYPTO.DECLINE, { id, status, reason })
    if (data.success) return data?.response?.success
  }

  public async approveSubmittedCryptoTransaction(id: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.CRYPTO.SUBMITTED.APPROVE, { id, status })
    if (data.success) return data?.response?.success
  }

  public async declineSubmittedCryptoTransaction(id: string, reason: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.CRYPTO.SUBMITTED.DECLINE, {
      id,
      status,
      reason
    })
    if (data.success) return data?.response?.success
  }

  public async approveSubmittedPayadmitTransaction(id: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.PAYADMIT.SUBMITTED.APPROVE, { id, status })
    if (data.success) return data?.response?.success
  }

  public async declineSubmittedPayadmitTransaction(id: string, reason: string, status: string): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.PAYADMIT.SUBMITTED.DECLINE, {
      id,
      status,
      reason
    })
    if (data.success) return data?.response?.success
  }

  // SOFT-551: microservice mode can return the fail-closed `{ count: null, unavailable: true }` when
  // financial-service is down — the caller must hide the badge, never render the stale MNT shadow.
  public async pendingWithdrawalsCount(): Promise<{ count: number | null; unavailable?: boolean } | void> {
    const { data } = await useApi.get<IResponse<{ count: number | null; unavailable?: boolean }>>(
      REQ.ADMIN_DASHBOARD.TRANSACTIONS.PENDING_WITHDRAWALS_COUNT
    )
    if (data?.success) return data?.response
  }

  public async forceDeclineTransaction(id: string, reason: string, refund: boolean): Promise<boolean | void> {
    const { data } = await useApi.post<IResponse<TransactionActionResponse>>(REQ.ADMIN_DASHBOARD.FORCE_DECLINE, {
      id,
      reason,
      refund
    })
    if (data.success) return data?.response?.success
  }
}

export const transactionService = new TransactionService()
